// The slot types — the contracts that govern what content fits a socket and how
// to generate it. This is the single source of truth a socket's `slotType` and a
// model's `fits` resolve against: the viewer's socket picker filters by slot
// type, the MCP server lists slot types and their generation instructions, and
// the registry guardrail asserts every reference points at one of these.
//
// A slot type is primarily an AGENT BRIEF: its `instructions` are the prose
// contract an agent reads when generating content for the slot — dimensions,
// orientation, scale, visual style. The validation (defineSlotType) keeps them
// well-formed; the instructions keep the generated output well-shaped.

import { defineSlotType, type SlotType } from "../lib";

export const SLOT_TYPES: SlotType[] = [
  defineSlotType({
    id: "carry",
    label: "Carry",
    summary: "A small handheld object an avatar carries in front of its chest.",
    instructions: [
      "# Carry slot",
      "",
      "A small object cradled in the avatar's arms at chest height — a good,",
      "tool or resource the figure is hauling. The avatar publishes a `carry`",
      "socket at roughly `[0, 0.12, 0.13]` (chest-front in model space); the",
      "content's own origin lands there.",
      "",
      "## Dimensions",
      "- Max extent: ~0.2 wide (x), ~0.15 tall (y), ~0.15 deep (z).",
      "- Author about the origin at the object's centre-bottom, so the base",
      "  sits at `y = 0`.",
      "",
      "## Orientation",
      "- The object faces +z (same as the carrier). Long axis along x",
      "  (crosswise in the arms, like a bundle of logs) or along y (upright,",
      "  like a sack).",
      "",
      "## Visual style",
      "- Low-poly, flat-coloured, no textures — the same language as every",
      "  model in the library.",
      "- Keep it small and readable at the avatar's scale (~0.35 tiles tall):",
      "  a few primitives, bold colours, a clear silhouette.",
      "- Reuse the shared palette (bark, burlap, metal, grain, crystal...).",
      "",
      "## Naming",
      "- Tag the model with the resource it represents (e.g. `wood`, `grain`,",
      "  `ore`). The `good` and `cargo` tags are conventional for trade goods.",
      "",
      "## Reference models",
      "- `wood-bundle` — three stacked logs (the wood good).",
      "- `grain-sack` — a tied burlap sack (the grain good).",
    ].join("\n"),
    description:
      "The carry slot is the avatar's primary goods-hauling mount point. " +
      "Every carriable good in the library fits it (wood-bundle, grain-sack); " +
      "new goods should follow the same scale and orientation contract so they " +
      "look right cradled in any avatar's arms.",
  }),

  defineSlotType({
    id: "draft",
    label: "Draft",
    summary: "A draught animal hitched to a vehicle's draw pole or harness.",
    instructions: [
      "# Draft slot",
      "",
      "A draught animal hitched to a wagon, cart or other vehicle — positioned",
      "in front of the vehicle along the draw pole / harness. The vehicle",
      "publishes draft sockets in tiered groups (1 large, 2 medium, 4 small,",
      "8 tiny); the consumer fills exactly one tier at a time.",
      "",
      "## Dimensions",
      "- The socket's `scale` already sizes the animal relative to the vehicle.",
      "- Author the animal about its centre-bottom origin (feet at y = 0),",
      "  facing +z (the direction of travel).",
      "",
      "## Orientation",
      "- The animal faces +z (forward, same as the vehicle). Its long axis is",
      "  along z.",
      "",
      "## Visual style",
      "- Low-poly, flat-coloured, no textures — the same language as every",
      "  model in the library.",
      "- The animal should read clearly at the vehicle's scale: bold silhouette,",
      "  few primitives.",
      "",
      "## Reference models",
      "- Any quadruped or bird model tagged `animal` or `creature`.",
    ].join("\n"),
    description:
      "The draft slot is the vehicle's animal-power mount point. " +
      "Draft sockets come in four mutually exclusive tiers (1 large, " +
      "2 medium, 4 small, 8 tiny); the consumer fills exactly one tier " +
      "so the team reads as a coherent hitch.",
  }),
];

export const slotTypeById: ReadonlyMap<string, SlotType> = new Map(
  SLOT_TYPES.map((s) => [s.id, s]),
);

export function slotTypeOf(socket: { slotType?: string }): SlotType | undefined {
  return socket.slotType ? slotTypeById.get(socket.slotType) : undefined;
}
